import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { listFunds } from "@/lib/api/funds";
import { listLps } from "@/lib/api/lps";
import { listCommitmentsByFund } from "@/lib/api/commitments";
import { listProvisionsByFund } from "@/lib/api/provisions";
import { listMfnTriggers, TRIGGER_STATUS_LABELS } from "@/lib/api/mfn";
import { listObligations } from "@/lib/api/obligations";
import { listAuditEvents } from "@/lib/api/audit";
import { CATEGORY_LABELS } from "@argus/engine";

/**
 * Audit evidence pack — build doc §4.2 Flow D / P0 "Export (CSV, PDF audit pack)".
 * A print-clean compliance report per fund; the browser's print-to-PDF is the
 * export mechanism (no PDF library, no server round trip, nothing to maintain).
 */

const fmtUsd = (n: number): string =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 1 })}M`
    : `$${n.toLocaleString("en-US")}`;

function AuditPackInner() {
  const tenant = useTenant();
  const params = useParams({ strict: false });
  const fundId = params.fundId ?? "";

  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const fund = funds?.find((f) => f.id === fundId);

  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });
  const { data: commitments } = useQuery({
    queryKey: ["commitments", tenant.id, fundId],
    queryFn: () => listCommitmentsByFund(tenant.id, fundId),
    enabled: !!fundId,
  });
  const { data: provisions } = useQuery({
    queryKey: ["provisions-fund", tenant.id, fundId],
    queryFn: () => listProvisionsByFund(tenant.id, fundId, { activeOnly: true }),
    enabled: !!fundId,
  });
  const { data: triggers } = useQuery({
    queryKey: ["mfn-triggers", tenant.id, true],
    queryFn: () => listMfnTriggers(tenant.id, {}),
  });
  const { data: obligations } = useQuery({
    queryKey: ["obligations", tenant.id, true],
    queryFn: () => listObligations(tenant.id, {}),
  });
  const { data: auditEvents } = useQuery({
    queryKey: ["audit", tenant.id, "", "pack"],
    queryFn: () => listAuditEvents(tenant.id, { limit: 100 }),
  });

  if (!fund) {
    return (
      <p className="text-sm text-neutral-500">
        Fund not found.{" "}
        <Link to="/t/$slug/funds" params={{ slug: tenant.slug }} className="underline">
          Back to funds
        </Link>
      </p>
    );
  }

  const lpName = (id: string | null): string =>
    id === null ? "Fund-level" : (lps?.find((l) => l.id === id)?.legal_name ?? "—");
  const fundTriggers = triggers?.filter((t) => t.fund_id === fundId) ?? [];
  const fundObligations = obligations?.filter((o) => o.fund_id === fundId) ?? [];
  const confirmed = provisions?.filter(
    (p) => p.review_status === "confirmed" || p.review_status === "edited",
  );
  const pending = provisions?.filter((p) => p.review_status === "pending");

  return (
    <div className="print:text-black">
      <header className="flex items-start justify-between print:hidden">
        <div>
          <Link
            to="/t/$slug/audit"
            params={{ slug: tenant.slug }}
            className="text-xs text-neutral-500 hover:text-neutral-300"
          >
            ← Audit trail
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Audit evidence pack</h1>
          <p className="text-sm text-neutral-400">
            Print-ready compliance report. Use your browser&rsquo;s print dialog → &ldquo;Save as
            PDF&rdquo;.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 hover:bg-white"
        >
          Print / Save as PDF
        </button>
      </header>

      <article className="mt-4 space-y-6 rounded border border-neutral-800 bg-white p-8 text-neutral-900 print:mt-0 print:rounded-none print:border-0 print:p-0">
        <header className="border-b border-neutral-300 pb-4">
          <h1 className="text-2xl font-bold">Side Letter Compliance Report</h1>
          <p className="mt-1 text-sm text-neutral-600">
            {tenant.name} · {fund.name}
            {fund.vintage_year ? ` · Vintage ${fund.vintage_year}` : ""}
            {fund.fund_size_usd ? ` · ${fmtUsd(Number(fund.fund_size_usd))}` : ""}
          </p>
          <p className="text-xs text-neutral-500">
            Generated {new Date().toLocaleString()} by Argus. AI-assisted extractions are starting
            points; statuses below reflect human review decisions recorded in the audit trail.
          </p>
        </header>

        <section>
          <h2 className="text-lg font-semibold">1. Summary</h2>
          <table className="mt-2 text-sm">
            <tbody>
              <tr>
                <td className="pr-8 text-neutral-600">Limited partners with commitments</td>
                <td className="font-medium">{commitments?.length ?? 0}</td>
              </tr>
              <tr>
                <td className="pr-8 text-neutral-600">Active provisions (confirmed / pending)</td>
                <td className="font-medium">
                  {confirmed?.length ?? 0} / {pending?.length ?? 0}
                </td>
              </tr>
              <tr>
                <td className="pr-8 text-neutral-600">MFN triggers (open / total)</td>
                <td className="font-medium">
                  {fundTriggers.filter((t) => t.status === "open").length} / {fundTriggers.length}
                </td>
              </tr>
              <tr>
                <td className="pr-8 text-neutral-600">Obligations tracked</td>
                <td className="font-medium">{fundObligations.length}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-lg font-semibold">2. Commitments</h2>
          <table className="mt-2 w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-left text-xs uppercase text-neutral-500">
                <th className="py-1 pr-4">Limited partner</th>
                <th className="py-1 pr-4">Commitment</th>
                <th className="py-1">Closing</th>
              </tr>
            </thead>
            <tbody>
              {commitments?.map((c) => (
                <tr key={c.id} className="border-b border-neutral-200">
                  <td className="py-1 pr-4">{lpName(c.lp_id)}</td>
                  <td className="py-1 pr-4">{fmtUsd(Number(c.commitment_usd))}</td>
                  <td className="py-1">{c.closing_date ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-lg font-semibold">3. Provisions by limited partner</h2>
          {lps
            ?.filter((lp) => provisions?.some((p) => p.lp_id === lp.id))
            .map((lp) => (
              <div key={lp.id} className="mt-3">
                <h3 className="font-medium">{lp.legal_name}</h3>
                <table className="mt-1 w-full border-collapse text-sm">
                  <tbody>
                    {provisions
                      ?.filter((p) => p.lp_id === lp.id)
                      .map((p) => (
                        <tr key={p.id} className="border-b border-neutral-200 align-top">
                          <td className="w-48 py-1 pr-4 font-medium">
                            {CATEGORY_LABELS[p.category]}
                          </td>
                          <td className="py-1 pr-4 text-neutral-700">{p.summary}</td>
                          <td className="w-24 py-1 text-xs uppercase text-neutral-500">
                            {p.review_status}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ))}
        </section>

        <section className="break-before-page">
          <h2 className="text-lg font-semibold">4. MFN triggers and resolutions</h2>
          {fundTriggers.length === 0 ? (
            <p className="mt-1 text-sm text-neutral-600">No triggers recorded.</p>
          ) : (
            <table className="mt-2 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-300 text-left text-xs uppercase text-neutral-500">
                  <th className="py-1 pr-4">Holder</th>
                  <th className="py-1 pr-4">Beneficiary</th>
                  <th className="py-1 pr-4">Category</th>
                  <th className="py-1 pr-4">Status</th>
                  <th className="py-1">Resolution notes</th>
                </tr>
              </thead>
              <tbody>
                {fundTriggers.map((t) => (
                  <tr key={t.id} className="border-b border-neutral-200 align-top">
                    <td className="py-1 pr-4">{lpName(t.holder_lp_id)}</td>
                    <td className="py-1 pr-4">{lpName(t.beneficiary_lp_id)}</td>
                    <td className="py-1 pr-4">{CATEGORY_LABELS[t.category]}</td>
                    <td className="py-1 pr-4">{TRIGGER_STATUS_LABELS[t.status]}</td>
                    <td className="py-1 text-neutral-700">{t.resolution_notes ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold">5. Obligation calendar</h2>
          <table className="mt-2 w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-left text-xs uppercase text-neutral-500">
                <th className="py-1 pr-4">Obligation</th>
                <th className="py-1 pr-4">LP</th>
                <th className="py-1 pr-4">Next due</th>
                <th className="py-1 pr-4">Last completed</th>
                <th className="py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {fundObligations.map((o) => (
                <tr key={o.id} className="border-b border-neutral-200">
                  <td className="py-1 pr-4">{o.title}</td>
                  <td className="py-1 pr-4">{lpName(o.lp_id)}</td>
                  <td className="py-1 pr-4">{o.next_due_date ?? "—"}</td>
                  <td className="py-1 pr-4">{o.last_completed_date ?? "—"}</td>
                  <td className="py-1">{o.status.replaceAll("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-lg font-semibold">6. Recent audit events (latest 100)</h2>
          <table className="mt-2 w-full border-collapse text-xs">
            <tbody>
              {auditEvents?.map((e) => (
                <tr key={e.id} className="border-b border-neutral-100">
                  <td className="w-44 py-0.5 pr-4 text-neutral-500">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="w-20 py-0.5 pr-4 text-neutral-500">{e.actor_type}</td>
                  <td className="py-0.5 font-mono">{e.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </article>
    </div>
  );
}

export function AuditPackPage() {
  return (
    <TenantLayout>
      <AuditPackInner />
    </TenantLayout>
  );
}
